import { TrendingUp, TrendingDown, type LucideIcon } from 'lucide-react';
import type { FC } from 'react';

interface StatCardProps {
  title: string;
  value: string | number | undefined;
  icon: LucideIcon;
  iconColor?: string;
  iconBg?: string;
  trend?: { value: number; isPositive: boolean; label?: string };
  loading?: boolean;
}

const StatCard: FC<StatCardProps> = ({
  title,
  value,
  icon: Icon,
  iconColor = 'text-primary-600 dark:text-primary-400',
  iconBg = 'bg-primary-50 dark:bg-primary-900/30',
  trend,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="fc-card p-5 animate-pulse">
        <div className="flex items-start justify-between">
          <div className="space-y-3 flex-1">
            <div className="h-3 w-20 rounded bg-slate-200 dark:bg-slate-700" />
            <div className="h-8 w-16 rounded bg-slate-200 dark:bg-slate-700" />
          </div>
          <div className="h-10 w-10 rounded-xl bg-slate-200 dark:bg-slate-700" />
        </div>
      </div>
    );
  }

  return (
    <div className="fc-card p-5 group">
      <div className="flex items-start justify-between">
        <div className="space-y-1 flex-1 min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {title}
          </p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">
            {value ?? '—'}
          </p>
          {trend && (
            <div className={`flex items-center gap-1 text-xs font-medium ${
              trend.isPositive
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-red-600 dark:text-red-400'
            }`}>
              {trend.isPositive ? (
                <TrendingUp className="w-3.5 h-3.5" />
              ) : (
                <TrendingDown className="w-3.5 h-3.5" />
              )}
              <span>+{trend.value}</span>
              {trend.label && (
                <span className="text-slate-400 dark:text-slate-500 font-normal">
                  {trend.label}
                </span>
              )}
            </div>
          )}
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${iconBg} transition-transform duration-200 group-hover:scale-110`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
      </div>
    </div>
  );
};

export default StatCard;
